<template>
    <div v-if="props.event" class="bg-white dark:bg-gray-800">
        <!-- Event Header -->
        <div class="p-4 border-b border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                    <div 
                        class="w-4 h-4 rounded-full"
                        :class="getEventColor(event)"
                        :style="getEventStyles(event)"
                    ></div>
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                        {{ event.title }}
                    </h3>
                </div>
            </div>
        </div>

        <!-- Event Details -->
        <div class="p-4 space-y-4">
            <!-- Date and Time -->
            <div v-if="isMultiDay(event)" class="grid grid-cols-2 gap-4">
                <div>
                    <label class="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {{ $t('schedule.eventForm.startDate') ? $t('schedule.eventForm.startDate').replace('*', '').trim() : 'Start Date' }}
                    </label>
                    <p class="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                        {{ formatEventStartDate(event) }}
                    </p>
                    <p v-if="event.startTime" class="text-xs text-indigo-600 dark:text-indigo-400 font-medium mt-0.5">
                        {{ formatEventStartTime(event) }}
                    </p>
                </div>
                <div>
                    <label class="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {{ $t('schedule.eventForm.endDate') ? $t('schedule.eventForm.endDate').replace('*', '').trim() : 'End Date' }}
                    </label>
                    <p class="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                        {{ formatEventEndDate(event) }}
                    </p>
                    <p v-if="event.endTime" class="text-xs text-indigo-600 dark:text-indigo-400 font-medium mt-0.5">
                        {{ formatEventEndTime(event) }}
                    </p>
                </div>
            </div>
            <div v-else class="grid grid-cols-2 gap-4">
                <div>
                    <label class="text-sm font-medium text-gray-500 dark:text-gray-400">Date</label>
                    <p class="text-sm text-gray-900 dark:text-white mt-1">
                        {{ formatEventDate(event) }}
                    </p>
                </div>
                <div v-if="event.startTime">
                    <label class="text-sm font-medium text-gray-500 dark:text-gray-400">Time</label>
                    <p class="text-sm text-gray-900 dark:text-white mt-1">
                        {{ formatEventTime(event) }}
                    </p>
                </div>
            </div>

            <!-- Duration -->
            <div v-if="event.startTime && event.endTime">
                <label class="text-sm font-medium text-gray-500 dark:text-gray-400">Duration</label>
                <p class="text-sm text-gray-900 dark:text-white mt-1">
                    {{ formatEventDuration(event) }}
                </p>
            </div>

            <!-- Category -->
            <div>
                <label class="text-sm font-medium text-gray-500 dark:text-gray-400">Category</label>
                <div class="flex items-center mt-1">
                    <div 
                        class="w-3 h-3 rounded-full mr-2"
                        :class="getEventColor(event)"
                        :style="getEventStyles(event)"
                    ></div>
                    <div class="text-sm text-gray-900 dark:text-white">
                        {{ $te(`schedule.categories.${event.category}`) ? $t(`schedule.categories.${event.category}`) : event.category }}
                    </div>
                </div>
            </div>

            <!-- Description -->
            <div v-if="event.description">
                <label class="text-sm font-medium text-gray-500 dark:text-gray-400">Description</label>
                <p class="text-sm text-gray-900 dark:text-white mt-1">
                    {{ event.description }}
                </p>
            </div>

            <!-- Address -->
            <div v-if="event.address">
                <label class="text-sm font-medium text-gray-500 dark:text-gray-400">Address</label>
                <p class="text-sm text-gray-900 dark:text-white mt-1">
                    {{ event.address }}
                </p>
            </div>

            <!-- Livestream Link -->
            <div v-if="event.livestreamLink">
                <label class="text-sm font-medium text-gray-500 dark:text-gray-400">Livestream Link</label>
                <p class="text-sm text-gray-900 dark:text-white mt-1">
                    <a :href="event.livestreamLink" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 underline">
                        {{ event.livestreamLink }}
                    </a>
                </p>
            </div>

            <!-- Participants -->
            <div v-if="participants && (participants.users?.length > 0 || participants.teams?.length > 0)">
                <label class="text-sm font-medium text-gray-500 dark:text-gray-400">Participants</label>
                <div class="mt-2 space-y-2">
                    <!-- Users -->
                    <div v-if="participants.users?.length > 0">
                        <div class="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Users:</div>
                        <div class="space-y-1">
                            <div v-for="user in participants.users" 
                                :key="'detail-user-' + user.id"
                                class="flex items-center p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                                <svg class="w-4 h-4 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21c-2.17 0-5.216-.584-7.499-1.632Z" />
                                </svg>
                                <span class="text-sm text-gray-900 dark:text-white">
                                    {{ user.name ? `${user.name} (${user.email})` : user.email }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Teams -->
                    <div v-if="participants.teams?.length > 0">
                        <div class="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Teams:</div>
                        <div class="space-y-1">
                            <div v-for="team in participants.teams" 
                                :key="'detail-team-' + team._id"
                                class="flex items-center p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                <svg class="w-4 h-4 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                                </svg>
                                <span class="text-sm text-gray-900 dark:text-white">
                                    {{ team.name }} ({{ team.memberCount }} members)
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Linked Playlists / Repertoires -->
            <div>
                <label class="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {{ $t('schedule.playlists') || 'Linked Playlists' }}
                </label>
                <div v-if="playlists && playlists.length > 0" class="mt-2 space-y-2">
                    <div 
                        v-for="pl in playlists" 
                        :key="'detail-playlist-' + (pl.id || pl._id)"
                        @click="openPlaylist(pl)"
                        class="flex items-center justify-between p-2.5 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800/40 hover:bg-purple-100/70 dark:hover:bg-purple-900/40 transition-colors cursor-pointer"
                    >
                        <div class="flex items-center space-x-2.5 truncate">
                            <svg class="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m9 9 10.5-3m0 6.553v3.75a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 1 1-.99-3.467l2.31-.66a.75.75 0 0 0 .582-.728V6.75A.75.75 0 0 0 18.19 6L9.69 8.428A.75.75 0 0 0 9 9.15v7.653a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 1 1-.99-3.467l2.31-.66A.75.75 0 0 0 7.5 14.5V9Z" />
                            </svg>
                            <span class="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {{ pl.name }}
                            </span>
                        </div>
                        <button 
                            type="button" 
                            @click.stop="openPlaylist(pl)"
                            class="text-xs font-semibold text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 flex items-center space-x-1 ml-2 flex-shrink-0"
                        >
                            <span>{{ $t('schedule.openPlaylist') || 'Open' }}</span>
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div v-else class="text-xs text-gray-400 dark:text-gray-500 mt-1 italic">
                    {{ $t('schedule.noPlaylists') || 'No playlists linked' }}
                </div>
            </div>

            <!-- Location Map -->
            <MapboxStaticMap v-if="event.address" :address="event.address" />

            <!-- Actions -->
            <div class="flex space-x-2 pt-4 border-t border-gray-200 dark:border-gray-700" v-if="event.isOwner">
                <button 
                    @click="emit('edit-event', event)"
                    class="flex-1 px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 border border-indigo-600 dark:border-indigo-400 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                >
                    Edit Event
                </button>
                <button 
                    @click="handleDeleteEvent(event)"
                    class="flex-1 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-600 dark:border-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                    Delete Event
                </button>
            </div>
        </div>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
        </svg>
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Event Selected
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
            Select an event from the calendar to view its details
        </p>
    </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue'
import { isTailwindColor } from '@/utilities/color'
import MapboxStaticMap from './MapboxStaticMap.vue'
import dialogStore from '@/store/dialog.store'
import ScheduleService from '@/service/schedule.service'
import { useI18n } from 'vue-i18n'

// Props
const props = defineProps({
    event: {
        type: Object,
        default: null
    },
    categories: {
        type: Array,
        default: () => []
    }
})

// Emits
const emit = defineEmits(['edit-event', 'delete-event', 'open-playlist'])

const { locale } = useI18n()
const playlists = ref([])
const participants = ref({ users: [], teams: [] })

const openPlaylist = (playlist) => {
    emit('open-playlist', playlist)
}

watch(() => props.event, async (newEvent) => {
    if (newEvent) {
        playlists.value = newEvent.playlists ? [...newEvent.playlists] : []
        participants.value = {
            users: newEvent.participants?.users || [],
            teams: newEvent.participants?.teams || []
        }

        const eventId = newEvent.id || newEvent._id
        if (eventId) {
            try {
                const detailedEvent = await ScheduleService.get(eventId)
                if (detailedEvent && !detailedEvent.error && detailedEvent.playlists) {
                    playlists.value = detailedEvent.playlists
                }
            } catch (err) {
                console.error('Error fetching event details in EventDetails:', err)
            }

            try {
                const p = await ScheduleService.fetchParticipants(eventId)
                if (p && !p.error) {
                    participants.value = {
                        users: p.users || [],
                        teams: p.teams || []
                    }
                }
            } catch (err) {
                console.error('Error fetching participants in EventDetails:', err)
            }
        }
    } else {
        playlists.value = []
        participants.value = { users: [], teams: [] }
    }
}, { immediate: true })

// Methods
const isMultiDay = (event) => {
    if (!event) return false
    const start = event.startTime ? new Date(event.startTime) : (event.date ? new Date(event.date) : null)
    const end = event.endTime ? new Date(event.endTime) : null
    if (!start || !end) return false
    return start.toDateString() !== end.toDateString()
}

const formatEventStartDate = (event) => {
    const target = event.startTime ? new Date(event.startTime) : (event.date ? new Date(event.date) : null)
    if (!target) return ''
    return target.toLocaleDateString(locale.value || 'en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    })
}

const formatEventEndDate = (event) => {
    const target = event.endTime ? new Date(event.endTime) : null
    if (!target) return ''
    return target.toLocaleDateString(locale.value || 'en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    })
}

const formatEventStartTime = (event) => {
    if (!event.startTime) return ''
    return new Date(event.startTime).toLocaleTimeString(locale.value || 'en-US', { 
        hour: 'numeric', 
        minute: '2-digit' 
    })
}

const formatEventEndTime = (event) => {
    if (!event.endTime) return ''
    return new Date(event.endTime).toLocaleTimeString(locale.value || 'en-US', { 
        hour: 'numeric', 
        minute: '2-digit' 
    })
}

const getEventStyles = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    
    if (isTailwindColor(color)) {
        return {}
    }
    return {
        backgroundColor: color
    }
}

const getEventColor = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    
    if (isTailwindColor(color)) {
        return `bg-${color}-500`
    }
    return ''
}

const formatEventDate = (event) => {
    let target = null
    if (event.startTime) {
        target = event.startTime instanceof Date ? event.startTime : new Date(event.startTime)
    } else if (event.date) {
        if (event.date instanceof Date) {
            target = event.date
        } else if (typeof event.date === 'string') {
            const [y, m, d] = event.date.slice(0, 10).split('-').map(Number)
            target = new Date(y, m - 1, d)
        }
    }
    if (!target) return ''
    return target.toLocaleDateString(locale.value || 'en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    })
}

const formatEventTime = (event) => {
    if (!event.startTime) return ''
    const startTime = new Date(event.startTime).toLocaleTimeString(locale.value || 'en-US', { 
        hour: 'numeric', 
        minute: '2-digit' 
    })
    const endTime = event.endTime ? new Date(event.endTime).toLocaleTimeString(locale.value || 'en-US', { 
        hour: 'numeric', 
        minute: '2-digit' 
    }) : ''
    return endTime ? `${startTime} - ${endTime}` : startTime
}

const formatEventDuration = (event) => {
    if (!event.startTime || !event.endTime) return ''
    const start = new Date(event.startTime)
    const end = new Date(event.endTime)
    const duration = (end - start) / (1000 * 60) // minutes
    const hours = Math.floor(duration / 60)
    const minutes = Math.round(duration % 60)
    if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
    }
    return `${minutes}m`
}

const handleDeleteEvent = async (event) => {
    if (await dialogStore.dispatch('confirm', `Are you sure you want to delete the event: ${event.title}?`)) {
        emit('delete-event', event)
    }
}
</script>